// ✅ src/pages/OrderManagement/BOQPrint.jsx  (FULL FILE)
// Fetches BOQ rows + header (company/project/address) from SAME API response.
// Backend should return:
// { success:true, header:{companyName, companyAddress, projectName, contractId}, list:[...] }

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = "http://nissi-india.com/api/dashboardDetails";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const fmtMoney2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtMoney0 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

const BOQPrint = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [header, setHeader] = useState({
    companyName: "",
    companyAddress: "",
    projectName: "",
  });

  // ✅ localStorage helper
  const getLS = (key) => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(key) || "";
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ read query params
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const contractId = useMemo(() => {
    const cid = qs.get("contractid") || qs.get("contractId") || "";
    const n = Number(cid || 0);
    return Number.isFinite(n) ? n : 0;
  }, [qs]);

  useEffect(() => {
    const load = async () => {
      if (!contractId) {
        setError("contractId not found in URL. Example: /OrderManagement/BOQPrint?contractid=5");
        setRows([]);
        setHeader({ companyName: "", companyAddress: "", projectName: "" });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API_BASE}/boq`, {
          params: { contractId },
          headers: commonHeaders,
        });

        const list = res.data?.list || [];
        setRows(list);

        // ✅ Prefer header from API, else fallback from first row
        const h = res.data?.header;
        if (h && (h.companyName || h.companyAddress || h.projectName)) {
          setHeader({
            companyName: h.companyName || "",
            companyAddress: h.companyAddress || "",
            projectName: h.projectName || "",
          });
        } else {
          const first = list?.[0] || {};
          setHeader({
            companyName: first.fld_Customername || "",
            companyAddress: first.fld_Customeraddress || "",
            projectName: first.fld_Projectname || "",
          });
        }
      } catch (e) {
        setRows([]);
        setHeader({ companyName: "", companyAddress: "", projectName: "" });
        setError(e?.response?.data?.message || e?.message || "Failed to load BOQ");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [contractId, commonHeaders]);

  const data = useMemo(() => {
    return (rows || []).map((r, idx) => ({
      sno: idx + 1,
      category: r.fld_projectcategoryname || "",
      work: r.fld_work || "",
      unit: r.fld_unit || "",
      boqRate: toNum(r.fld_rateperunit),
      boqQty: toNum(r.fld_Qty),
      boqAmt: toNum(r.amount),
      opRate: toNum(r.fld_oprateperunit),
      opQty: toNum(r.fld_opQty),
      opAmt: toNum(r.fld_opamount),
    }));
  }, [rows]);

  const totals = useMemo(() => {
    return data.reduce(
      (acc, x) => {
        acc.boqAmt += toNum(x.boqAmt);
        acc.opAmt += toNum(x.opAmt);
        return acc;
      },
      { boqAmt: 0, opAmt: 0 }
    );
  }, [data]);

  const onPrint = () => window.print();
  const onClose = () => window.close();

  return (
    <div className="min-h-screen bg-gray-100 p-3 print:p-0">
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      <div className="no-print max-w-[900px] mx-auto mb-3 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          <span className="font-semibold">BOQ Operative Report</span> • Contract ID: {contractId || "-"}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onPrint}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            Print
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>

      <div className="print-sheet bg-white max-w-[900px] mx-auto shadow-xl border border-gray-300">
        <div className="p-5">
          <div className="border-2 border-gray-800 p-4">
            <div className="flex items-center gap-4">
              <div className="w-28 h-16 border border-gray-800 flex items-center justify-center text-xs font-semibold">
                LOGO
              </div>

              <div className="flex-1 text-center">
                <div className="text-lg font-bold leading-tight">Nissi Infotech</div>
                <div className="text-xs font-semibold">CIVIL CONSTRUCTION</div>
                <div className="text-[11px] mt-1 leading-snug">
                  3/42-12, Nagappa colony, Kavundampalayam, Tamil Nadu
                  <br />
                  Phone No : 9003744644
                </div>
              </div>

              <div className="w-28" />
            </div>

            <div className="mt-3 border-t border-gray-800 pt-3">
              <div className="text-center font-bold underline">BOQ Operative Report</div>

              <div className="mt-4 grid grid-cols-2 gap-6 text-[12px]">
                <div className="space-y-1">
                  <div className="flex">
                    <div className="w-28 font-semibold">Company Name</div>
                    <div className="w-3">:</div>
                    <div className="flex-1">{header.companyName || "-"}</div>
                  </div>
                  <div className="flex">
                    <div className="w-28 font-semibold">Company Address</div>
                    <div className="w-3">:</div>
                    <div className="flex-1">{header.companyAddress || "-"}</div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex">
                    <div className="w-24 font-semibold">Project Name</div>
                    <div className="w-3">:</div>
                    <div className="flex-1">{header.projectName || "-"}</div>
                  </div>
                  <div className="flex">
                    <div className="w-24 font-semibold">Contract ID</div>
                    <div className="w-3">:</div>
                    <div className="flex-1">{contractId || "-"}</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 border border-gray-800">
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-800 p-2 w-12">Sno</th>
                      <th className="border border-gray-800 p-2 w-28">Category</th>
                      <th className="border border-gray-800 p-2">Nature Of Work</th>
                      <th className="border border-gray-800 p-2 w-16">Unit</th>
                      <th className="border border-gray-800 p-2 w-20 text-right">BOQ_Rate</th>
                      <th className="border border-gray-800 p-2 w-20 text-right">BOQ_Qty</th>
                      <th className="border border-gray-800 p-2 w-24 text-right">BOQ_Amt</th>
                      <th className="border border-gray-800 p-2 w-20 text-right">OP_Rate</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="border border-gray-800 p-3 text-center" colSpan={8}>
                          Loading...
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td className="border border-gray-800 p-3 text-center text-red-600" colSpan={8}>
                          {error}
                        </td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td className="border border-gray-800 p-3 text-center" colSpan={8}>
                          No Record Found
                        </td>
                      </tr>
                    ) : (
                      data.map((x) => (
                        <tr key={x.sno}>
                          <td className="border border-gray-800 p-2 text-center align-top">{x.sno}</td>
                          <td className="border border-gray-800 p-2 align-top whitespace-pre-wrap">{x.category}</td>
                          <td className="border border-gray-800 p-2 align-top whitespace-pre-wrap">{x.work}</td>
                          <td className="border border-gray-800 p-2 align-top">{x.unit}</td>
                          <td className="border border-gray-800 p-2 text-right align-top">{fmtMoney2(x.boqRate)}</td>
                          <td className="border border-gray-800 p-2 text-right align-top">{fmtMoney2(x.boqQty)}</td>
                          <td className="border border-gray-800 p-2 text-right align-top">{fmtMoney2(x.boqAmt)}</td>
                          <td className="border border-gray-800 p-2 text-right align-top">{fmtMoney2(x.opRate)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>

                  {data.length > 0 && !loading && !error && (
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="border border-gray-800 p-2 text-right" colSpan={6}>
                          Total
                        </td>
                        <td className="border border-gray-800 p-2 text-right">{fmtMoney0(totals.boqAmt)}</td>
                        <td className="border border-gray-800 p-2" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <div className="mt-3 text-[11px] text-gray-700 flex justify-between">
                <div>Generated on: {new Date().toLocaleString("en-IN")}</div>
                <div className="font-semibold">BOQ Operative Report</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BOQPrint;